/**
 * @fileoverview PayDayPuffin Privacy Policy registry data.
 *
 * Hand-migrated faithfully from
 * `apps/payday-web/src/portals/marketing-website/pages/PrivacyPolicyPage.jsx`.
 *
 * payday-only Subprocessors section (#4) covers the Gusto, Inc.
 * subprocessor relationship per the private-label remediation
 * (Apr 11–12, 2026).
 */

/** @type {import('../../types.js').LegalDocument} */
export const privacy = Object.freeze({
  appKey: 'payday',
  documentKey: 'privacy',
  frontmatter: {
    title: 'Privacy Policy',
    productName: 'PayDayPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-04-11',
    contactEmail: 'hello@paydaypuffin.com',
    canonicalUrl: 'https://paydaypuffin.com/privacy',
    ogImage: 'https://paydaypuffin.com/og-image.jpg',
    description:
      'PayDayPuffin privacy policy. Learn how we collect, use, and protect your data, including payroll information handled by our authorized subprocessors.',
  },
  sections: [
    {
      id: 'introduction',
      title: '1. Introduction',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our payroll software services.',
        },
      ],
    },
    {
      id: 'information-we-collect',
      title: '2. Information We Collect',
      order: 2,
      blocks: [
        { type: 'paragraph', text: 'We may collect the following types of information:' },
        {
          type: 'list',
          items: [
            {
              strong: 'Contact Information',
              text: 'Name, email address, company name, and other details you provide through our contact form.',
            },
            {
              strong: 'Payroll Data',
              text: 'Timecard hours, employee compensation, tax identification, bank account details, and related records required to process payroll on your behalf.',
            },
            {
              strong: 'Usage Data',
              text: 'Browser type, IP address, pages visited, and other standard web analytics information.',
            },
            {
              strong: 'Audit and Operational Data',
              text: 'Authentication events, sync operations, payroll lifecycle transitions, and other metadata we retain for compliance, security, and debugging purposes.',
            },
          ],
        },
      ],
    },
    {
      id: 'how-we-use-your-information',
      title: '3. How We Use Your Information',
      order: 3,
      blocks: [
        { type: 'paragraph', text: 'We use the information we collect to:' },
        {
          type: 'list',
          items: [
            'Provide and maintain our payroll software services',
            'Process timecard data and submit payroll through our authorized subprocessors',
            'Respond to your contact form submissions',
            'Improve our services and user experience',
            'Maintain audit trails for compliance and regulatory purposes',
            'Send you service-related communications',
          ],
        },
      ],
    },
    {
      id: 'subprocessors',
      title: '4. Subprocessors',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} engages third-party service providers (subprocessors) to deliver certain components of our service. We maintain a complete list of our subprocessors, including their role, data categories processed, and location, at paydaypuffin.com/subprocessors.',
        },
        {
          type: 'paragraph',
          text: 'Gusto, Inc. (Delaware C-corporation) is our authorized subprocessor for payroll execution. They receive the payroll data necessary to issue wage payments, withhold and file taxes, and make direct deposits to your employees. You do not need a separate account with Gusto — your relationship is with {productName}, and our Terms of Service govern the processing relationship.',
        },
        {
          type: 'paragraph',
          text: "We require all subprocessors to meet contractual obligations protecting the confidentiality, integrity, and availability of your data. We will provide at least thirty (30) days' notice before engaging any new subprocessor, via posting to our subprocessors page and email notification to the primary account holder.",
        },
      ],
    },
    {
      id: 'data-security',
      title: '5. Data Security',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'We implement industry-standard security measures to protect your data, including encrypted connections (TLS), secure authentication (JWT), and append-only audit logging. However, no method of electronic transmission or storage is 100% secure.',
        },
      ],
    },
    {
      id: 'data-retention',
      title: '6. Data Retention',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'Contact form submissions are retained for two years. Payroll data and audit logs are retained in accordance with applicable regulatory requirements. You may request deletion of your data by contacting us.',
        },
      ],
    },
    {
      id: 'your-rights',
      title: '7. Your Rights',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'Depending on your location, you may have the right to access, correct, delete, or restrict the processing of your personal data. To exercise these rights, please contact us at {contactEmail}.',
        },
      ],
    },
    {
      id: 'changes-to-this-policy',
      title: '8. Changes to This Policy',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page with an updated effective date.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '9. Contact Us',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about this Privacy Policy, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
